import { MessageType } from '@/api/types/chat';
import { GroupNotificationEvent, GroupMessageEvent } from './ably-manager';

/**
 * 群组通知映射器
 * 负责将 MessageType 映射到对应的群组事件类型
 */
export class GroupNotificationMapper {
  
  /**
   * MessageType 到 GroupMessageEvent 的映射表（加密消息通知）
   */
  private static readonly MESSAGE_TYPE_TO_GROUP_MESSAGE_EVENT_MAP: Record<string, GroupMessageEvent> = {
    [MessageType.GROUP_NEW_CREATE]: GroupMessageEvent.group_new_create,
    [MessageType.GROUP_NEW_INVITE]: GroupMessageEvent.group_new_invite,
    [MessageType.GROUP_INFO_UPDATE]: GroupMessageEvent.group_info_update,
    [MessageType.GROUP_MEMBER_REMOVED]: GroupMessageEvent.group_member_removed,
    [MessageType.GROUP_DISSOLVE]: GroupMessageEvent.group_dissolve,
    [MessageType.GROUP_MEMBER_LEAVE]: GroupMessageEvent.group_member_leave,
    [MessageType.GROUP_MEMBER_ADDED]: GroupMessageEvent.group_member_added,
    [MessageType.GROUP_NOTICE_ADD]: GroupMessageEvent.group_notice_updated,
    [MessageType.GROUP_PINNED_MESSAGE_ADD]: GroupMessageEvent.group_pinned_message_add,
    [MessageType.GROUP_PINNED_MESSAGE_DELETE]: GroupMessageEvent.group_pinned_message_delete,
    [MessageType.GROUP_MUTE_ENABLED]: GroupMessageEvent.group_mute_enabled,
    [MessageType.GROUP_MUTE_DISABLED]: GroupMessageEvent.group_mute_disabled,
    [MessageType.GROUP_SCREENSHOT_ENABLED]: GroupMessageEvent.group_screenshot_enabled,
    [MessageType.GROUP_SCREENSHOT_DISABLED]: GroupMessageEvent.group_screenshot_disabled,
    [MessageType.GROUP_SEND_CARD_ENABLED]: GroupMessageEvent.group_send_card_enabled,
    [MessageType.GROUP_SEND_CARD_DISABLED]: GroupMessageEvent.group_send_card_disabled,
    [MessageType.GROUP_SEND_IMAGE_ENABLED]: GroupMessageEvent.group_send_image_enabled,
    [MessageType.GROUP_SEND_IMAGE_DISABLED]: GroupMessageEvent.group_send_image_disabled,
    [MessageType.GROUP_SEND_VIDEO_ENABLED]: GroupMessageEvent.group_send_video_enabled,
    [MessageType.GROUP_SEND_VIDEO_DISABLED]: GroupMessageEvent.group_send_video_disabled,
    [MessageType.GROUP_SEND_FILE_ENABLED]: GroupMessageEvent.group_send_file_enabled,
    [MessageType.GROUP_SEND_FILE_DISABLED]: GroupMessageEvent.group_send_file_disabled,
  };

  /**
   * 完整的 MessageType 到 GroupNotificationEvent 的映射表（向后兼容）
   */
  private static readonly MESSAGE_TYPE_TO_GROUP_EVENT_MAP: Record<string, GroupNotificationEvent> = {
    ...GroupNotificationMapper.MESSAGE_TYPE_TO_GROUP_MESSAGE_EVENT_MAP,
  };

  /**
   * 将 MessageType 转换为对应的 GroupMessageEvent（加密消息通知）
   * @param messageType 消息类型
   * @returns 对应的群组消息事件类型，如果不是群组消息类型则返回 null
   */
  static getGroupMessageEvent(messageType: MessageType): GroupMessageEvent | null {
    return this.MESSAGE_TYPE_TO_GROUP_MESSAGE_EVENT_MAP[messageType] || null;
  }

  /**
   * 将 MessageType 转换为对应的 GroupNotificationEvent（向后兼容）
   * @param messageType 消息类型
   * @returns 对应的群组通知事件类型，如果不是群组通知类型则返回 null
   */
  static getGroupNotificationEvent(messageType: MessageType): GroupNotificationEvent | null {
    return this.MESSAGE_TYPE_TO_GROUP_EVENT_MAP[messageType] || null;
  }

  /**
   * 检查消息类型是否为群组消息通知类型
   * @param messageType 消息类型
   * @returns 是否为群组消息通知类型
   */
  static isGroupMessageType(messageType: MessageType): boolean {
    return messageType in this.MESSAGE_TYPE_TO_GROUP_MESSAGE_EVENT_MAP;
  }

  /**
   * 检查消息类型是否为群组通知类型（向后兼容）
   * @param messageType 消息类型
   * @returns 是否为群组通知类型
   */
  static isGroupNotificationType(messageType: MessageType): boolean {
    return messageType in this.MESSAGE_TYPE_TO_GROUP_EVENT_MAP;
  }

  /**
   * 获取所有支持的群组消息通知类型
   * @returns 群组消息通知类型数组
   */
  static getSupportedGroupMessageTypes(): MessageType[] {
    return Object.keys(this.MESSAGE_TYPE_TO_GROUP_MESSAGE_EVENT_MAP) as MessageType[];
  }

  /**
   * 获取所有支持的群组通知消息类型（向后兼容）
   * @returns 群组通知消息类型数组
   */
  static getSupportedGroupNotificationMessageTypes(): MessageType[] {
    return Object.keys(this.MESSAGE_TYPE_TO_GROUP_EVENT_MAP) as MessageType[];
  }

  /**
   * 获取所有支持的群组消息事件类型
   * @returns 群组消息事件类型数组
   */
  static getSupportedGroupMessageEvents(): GroupMessageEvent[] {
    return Object.values(this.MESSAGE_TYPE_TO_GROUP_MESSAGE_EVENT_MAP);
  }

  /**
   * 获取所有支持的群组通知事件类型（向后兼容）
   * @returns 群组通知事件类型数组
   */
  static getSupportedGroupNotificationEvents(): GroupNotificationEvent[] {
    return Object.values(this.MESSAGE_TYPE_TO_GROUP_EVENT_MAP);
  }
}
